import React, { useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Button, Chip } from '@mui/material';

// Simple product data source. In real app, fetch by product slug.
const PRODUCT_DATA = {
  lipoderma: {
    key: 'lipoderma',
    displayName: 'Lipoderma',
    org: 'bare',
    clientPrice: 600.0,
    listPrice: 650.0,
    tiers: [
      { range: '1-9 units', price: '$650/ea', savings: '$100' },
      { range: '10-29 units', price: '$550/ea', savings: '$110' },
      { range: '30+ units', price: '$500/ea', savings: '$110' }
    ]
  },
  equicent: {
    key: 'equicent',
    displayName: 'equicent™ CTM',
    org: 'epl',
    clientPrice: 420.0,
    listPrice: 450.0,
    tiers: [
      { range: '1-9 units', price: '$450/ea', savings: '$74' },
      { range: '10-29 units', price: '$380/ea', savings: '$89' },
      { range: '30+ units', price: '$350/ea', savings: '$89' }
    ]
  }
};

const primary = '#039582';
const hover = '#027A6B';

const ClientPricing = () => {
  const { product } = useParams();

  const productData = useMemo(() => {
    const key = (product || '').toLowerCase();
    return PRODUCT_DATA[key] || null;
  }, [product]);

  // Persist last viewed selection for convenience
  useEffect(() => {
    if (productData) {
      try {
        localStorage.setItem('unisyn:selectedProduct', productData.key);
        localStorage.setItem('unisyn:selectedOrg', productData.org);
      } catch {}
    }
  }, [productData]);

  if (!productData) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Product not found
        </Typography>
        <Button variant="contained" component={Link} to="/products" sx={{ bgcolor: primary, '&:hover': { bgcolor: hover } }}>
          Back to Products
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
        {productData.displayName} Client Pricing
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Organization: <Chip label={productData.org === 'bare' ? 'Bare Biologics' : 'EPL'} size="small" sx={{ bgcolor: primary, color: 'white', fontWeight: 600 }} />
      </Typography>

      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 1 }}>
            <Typography sx={{ fontSize: 28, fontWeight: 700, color: primary }}>
              ${productData.clientPrice.toFixed(2)}
            </Typography>
            <Typography sx={{ textDecoration: 'line-through', color: '#888' }}>
              ${productData.listPrice.toFixed(2)}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Client exclusive pricing
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
            <Typography variant="subtitle2" sx={{ color: '#111' }}>Tier</Typography>
            <Typography variant="subtitle2" sx={{ color: '#111' }}>Your Price</Typography>
            <Typography variant="subtitle2" sx={{ color: '#111' }}>Savings</Typography>
            {productData.tiers.map((t, i) => (
              <React.Fragment key={i}>
                <Typography>{t.range}</Typography>
                <Typography sx={{ color: primary, fontWeight: 600 }}>{t.price}</Typography>
                <Typography sx={{ color: '#059669' }}>{t.savings}</Typography>
              </React.Fragment>
            ))}
          </Box>

          {/* Helper copy and examples */}
          <Box sx={{ mt: 3, p: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>How tier pricing works</Typography>
            <Typography variant="body2" sx={{ color: '#334155' }}>• Tiers apply per order based on total units of the same SKU.</Typography>
            <Typography variant="body2" sx={{ color: '#334155' }}>• Mix-and-match within the same SKU counts toward the tier total.</Typography>
            <Typography variant="body2" sx={{ color: '#334155' }}>• Different SKUs price independently.</Typography>
            <Typography variant="body2" sx={{ color: '#334155' }}>• All orders use Net 60 terms; pay within 10 days to receive a 10% early payment discount.</Typography>

            <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600 }}>Examples</Typography>
            <Typography variant="body2" sx={{ color: '#334155' }}>• 12 units in one order → the 10–29 tier applies to all 12.</Typography>
            <Typography variant="body2" sx={{ color: '#334155' }}>• 8 units now + 8 units later (separate orders) → each order priced at the 1–9 tier.</Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClientPricing;


